import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getService } from '../../../axios/axios';
import { YES, NO } from "../../../constants/constants";
import { Breadcrumb, BreadcrumbItem, Grid, GridColumn, useSnackbar } from "@livingdesign/react";
import moment from 'moment';
import { LoadingSkeleton } from "../../../components/LoadingSkeleton/LoadingSkeleton";
import ViewProductStyles from "./ViewProduct.styles";
import { getErrorMessage } from "../../../helpers/utils";
import { addProductConfig, getProductType } from "../../../components/Table/Columns/ProductListingColumns";
import { MOMENT_DATE_FORMAT } from "../../../constants/dateFormats";
import DisplayValue from "../../../components/TextField/DisplayValue/DisplayValue";
import { MATTER_SERVICE } from "../../../constants/baseURLs";
import { Article, Note, Receipt, User } from "@livingdesign/icons";

/**
 * Component to render add, edit and view Product page
 */
const ViewProduct = () => {
	const classes = ViewProductStyles();
	const { addSnack } = useSnackbar();
	const history = useHistory();

	const [properties, setProperties] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const { matterDetail } = useSelector((state) => state?.matter);
	const { state: { currentMatterTab, matterId, breadcrumbValues, productDetails = {} } = {} } = useLocation();
	const [pdtDetails, setPdtDetails] = useState({ ...productDetails, matterId: matterId, matterName: matterDetail?.matter?.matterName, matterNumber: matterDetail?.matter?.matterNumber });

	useEffect(() => {
		const property = addProductConfig()
		setProperties(property)
	}, [])

	useEffect(() => {
		setIsLoading(true);
		getService(MATTER_SERVICE, `/matter-products/v1/${pdtDetails?.id}`)
			.then((res) => {
				setPdtDetails(res?.data)
			})
			.catch((error) => {
				addSnack({
					message: getErrorMessage(error)
				});
			}).finally(() => { setIsLoading(false) });
	}, [])

	const DisplayValueText = (datatype, value) => {
		switch (datatype) {
			case "Boolean":
				return value ? YES : NO;
			case "Text":
				return value || null;
			case "Date":
				return value ? moment(value).format(MOMENT_DATE_FORMAT) : null;
			default:
				return value || null;
		}
	}

	const sections = ["Product Information", "Supplier Details", "Buyer Information", "Additional Notes"];

	const renderViewField = (property) => {
		const value = DisplayValueText(property?.dataType, pdtDetails[property?.propertyName]);
		const isEmpty = value === null || value === undefined || value === "" || value === "-";

		if (property?.dataType === "TextArea") {
			return (
				<div key={property?.propertyName} style={{ gridColumn: "1 / -1" }}>
					<div className={classes.fieldLabel}>{property?.title}</div>
					<div className={classes.notesBox}>
						{isEmpty ? "Not provided" : value}
					</div>
				</div>
			);
		}

		return (
			<div key={property?.propertyName} className={classes.fieldContainer}>
				<div className={classes.fieldLabel}>{property?.title}</div>
				<div className={isEmpty ? classes.fieldValueEmpty : classes.fieldValue}>
					{isEmpty ? "Not provided" : value}
				</div>
			</div>
		);
	}

	const getSectionIcon = (sectionName) => {
		switch (sectionName) {
			case "Product Information":
				return (
					<Receipt size="small" />
				);
			case "Supplier Details":
				return (
					<Article size="small" />
				);
			case "Buyer Information":
				return (
					<User size="small" />
				);
			case "Additional Notes":
				return (
					<Note size="small" />
				);
			default:
				return null;
		}
	}

	const renderSection = (sectionName) => {
		const sectionFields = properties?.filter(p => p?.section === sectionName);
		if (!sectionFields || sectionFields.length === 0) return null;

		return (
			<div key={sectionName} className={classes.sectionCard}>
				<div className={classes.sectionTitle}>
					{getSectionIcon(sectionName)}
					{sectionName}
				</div>
				<Grid UNSAFE_className={classes.sectionGridContainer}>
					{sectionFields.map(property => (
						<GridColumn
							key={property?.propertyName}
							sm={12}
							md={property?.dataType === "TextArea" ? 12 : 6}
							lg={property?.dataType === "TextArea" ? 12 : 3}
						>
							{renderViewField(property)}
						</GridColumn>
					))}
				</Grid>
			</div>
		);
	}

	return (
		<div className={classes?.pageContainer}>
			<div className={classes.headerContainer}>
				<div className={classes.breadcrumb}>
					<Breadcrumb>
						<BreadcrumbItem data-testid="breadcrumb-item-matter" onClick={() => history.push(`/matter/${matterId}`)}>Matter</BreadcrumbItem>
						<BreadcrumbItem data-testid="breadcrumb-item-matter-number" onClick={() => history.push(`/matter/${matterId}`)}>{matterDetail?.matter?.matterNumber || breadcrumbValues?.matterNumber}</BreadcrumbItem>
						<BreadcrumbItem data-testid="breadcrumb-item-products" onClick={() => history.push(`/matter/${matterId || pdtDetails?.matterId}`, { currentMatterTab })}>Products</BreadcrumbItem>
						<BreadcrumbItem isCurrent>Product Details</BreadcrumbItem>
					</Breadcrumb>
				</div>

				<div className={classes.headerRow}>
					<div className={classes.headerLeft}>
						<div className={classes.headerTitleRow}>
							<h1 className={classes.headerTitle}>Product Details</h1>
						</div>
					</div>
				</div>
			</div>

			{isLoading ? <LoadingSkeleton /> :

				<div data-testid="view-product">
					{sections.map(sectionName => renderSection(sectionName))}
				</div>
			}
		</div>
	)
};

export default ViewProduct;